/**
 * Toucan Music — bringing a collection onto the library.
 *
 * People keep albums in each of these sources.
 *
 * - **Liked albums** are Spotify's saved-albums library. The ids come with
 *   the albums, so nothing needs matching.
 * - **Album playlists** are playlists of your own whose every track belongs
 *   to one album. Spotify can't keep albums in folders, so people make a
 *   playlist per album, and the Web API can't see folders either, so these
 *   are found by their contents.
 * - **A pasted list** has one album per line, including a directory listing
 *   of a ripped MP3 library. Each line is searched for, and the matches are
 *   reviewed before anything is added, because a search can be wrong.
 *
 * Each source has its own screen, so one review never mixes two sources.
 * This file reads and decides, and `pages/ImportPage.tsx` shows it.
 */

import { describeError, listById, newId, required, showError } from "toucan";
import {
  addFoundAlbum,
  importLine,
  matchScore,
  parseAlbumLines,
  spotifyAlbumId,
  timeLeft,
} from "./model.ts";
import type { FoundAlbum, ImportLine } from "./model.ts";
import type { ImportSource } from "./routes.ts";
import { store } from "./state.ts";
import {
  albumsByIds,
  paced,
  pacedStopped,
  playlistAlbums,
  playlists,
  savedAlbums,
  searchAlbums,
  spotifyGapMs,
} from "./spotify.ts";
import type { PlaylistSummary } from "./spotify.ts";

/** A candidate for the library, with what was asked for and Spotify's offer. */
export interface Candidate {
  /** The source's name for it, from a pasted line, playlist or album. */
  label: string;
  /** The match, or null when nothing was found for the line. */
  found: FoundAlbum | null;
  /** Whether it will be added. Unmatched candidates cannot be. */
  chosen: boolean;
  /**
   * How well {@link found} answers the line, from 0 to 1. An id, your own
   * library and a match picked by hand all score 1.
   */
  score: number;
  /** The rest of what the search returned, best first. */
  options: FoundAlbum[];
  /** Whether those alternatives are on show. */
  showing: boolean;
}

/**
 * A playlist an import could not turn into an album, listed in the review.
 * Its name is often the album's, so the review offers a search for it.
 */
export interface Leftover {
  /** The playlist's name, which is also what a search for it looks up. */
  name: string;
  /** What stopped it: how many albums it holds, or that it could not be read. */
  reason: string;
}

/** What one run of a source came to. */
export interface ImportOutcome {
  candidates: Candidate[];
  leftovers: Leftover[];
  /** The name this import suggests for a shelf made from it, or "" for none. */
  shelfName: string;
}

/**
 * Reports a run's progress as one line, and roughly how long it has left when
 * it can estimate that. Called with two empty strings when the run is over.
 */
export type Progress = (message: string, left: string) => void;

/**
 * How many results a pasted line is looked up with. Only the best is added,
 * and the rest are offered as alternatives when it is wrong.
 */
const RESULTS_PER_LINE = 5;

/** A candidate there is nothing to decide about: an id, or your own library. */
function exact(label: string, found: FoundAlbum): Candidate {
  return { label, found, chosen: true, score: 1, options: [], showing: false };
}

/** A line nothing was found for. It cannot be added, so it cannot be chosen. */
function missed(label: string): Candidate {
  return {
    label,
    found: null,
    chosen: false,
    score: 0,
    options: [],
    showing: false,
  };
}

/** Each source's title, and a one-line summary of what it reads. */
export const SOURCES: Record<ImportSource, { title: string; summary: string }> =
  {
    liked: {
      title: "Liked albums",
      summary: "Every album saved to your Spotify library.",
    },
    playlists: {
      title: "Album playlists",
      summary: "Playlists you made whose tracks all belong to one album.",
    },
    list: {
      title: "A list you paste",
      summary:
        "One album per line — typed out, or a folder listing from a ripped library. Each line is searched for on Spotify.",
    },
  };

/** What the header calls the screen the route is on. */
export function importTitle(source: ImportSource | null): string {
  return source === null ? "Import albums" : SOURCES[source].title;
}

/** A run that found nothing, which is what a failed one leaves behind. */
const NOTHING: ImportOutcome = { candidates: [], leftovers: [], shelfName: "" };

/**
 * Report what a run is doing, with a rough time for the calls it has left.
 * Leave `callsLeft` out when the count isn't known yet, as while paging a
 * liked-albums library. The time counts only the gaps between calls, which
 * take much longer than the calls themselves.
 */
function say(report: Progress, message: string, callsLeft = 0): void {
  report(message, callsLeft === 0 ? "" : timeLeft(callsLeft * spotifyGapMs()));
}

/** Import every album saved to your Spotify library. */
export async function importLiked(
  report: Progress,
): Promise<ImportOutcome | null> {
  try {
    say(report, "Reading your liked albums…");
    const albums = await paced(savedAlbums);
    return {
      ...NOTHING,
      candidates: albums.map((found) => exact(found.name, found)),
    };
  } catch (error) {
    showError(`Could not read your liked albums: ${describeError(error)}`);
    return null;
  } finally {
    say(report, "");
  }
}

/**
 * The albums a playlist is made of, or null when Spotify refuses to say. A
 * refusal doesn't stop the import: the playlist goes on the review's "Not
 * imported" list instead.
 */
async function albumsInPlaylist(
  playlist: PlaylistSummary,
): Promise<FoundAlbum[] | null> {
  try {
    return await playlistAlbums(playlist.id);
  } catch {
    return null;
  }
}

/** Why a playlist did not become an album, in the words the list uses. */
function whyNot(albums: FoundAlbum[] | null): string {
  if (albums === null) {
    return "Spotify would not show its tracks";
  }
  if (albums.length === 0) {
    return "nothing in it Spotify could name";
  }
  return "more than one album in it";
}

/**
 * Import every playlist that is one album. This reads every playlist's
 * tracks, so it is the slow source.
 */
export async function importAlbumPlaylists(
  report: Progress,
): Promise<ImportOutcome | null> {
  const found: Candidate[] = [];
  const leftovers: Leftover[] = [];
  try {
    await paced(async () => {
      say(report, "Listing your playlists…");
      const all = await playlists();
      for (const [index, playlist] of all.entries()) {
        if (pacedStopped()) {
          break;
        }
        say(
          report,
          `Checking playlist ${String(index + 1)} of ${String(all.length)}: ${playlist.name}`,
          all.length - index,
        );
        // The listing gives each playlist's count, so an empty one costs no
        // request.
        if (playlist.tracks.total === 0) {
          leftovers.push({ name: playlist.name, reason: "nothing in it" });
          continue;
        }
        const albums = await albumsInPlaylist(playlist);
        // A playlist spanning several albums is a mixtape.
        if (albums?.length !== 1) {
          leftovers.push({ name: playlist.name, reason: whyNot(albums) });
          continue;
        }
        found.push(exact(playlist.name, required(albums[0], "album")));
      }
    });
    return {
      candidates: found,
      leftovers,
      // Only an import of one album offers its playlist's name for a new
      // shelf.
      shelfName:
        found.length === 1 ? required(found[0], "candidate").label : "",
    };
  } catch (error) {
    showError(`Could not read your playlists: ${describeError(error)}`);
    return null;
  } finally {
    say(report, "");
  }
}

/**
 * Search for a line and order the hits by how well each matches it. Spotify
 * doesn't give a score of its own, and its first hit is often a covers album
 * or a compilation of the same name.
 */
async function lookUpLine(line: ImportLine): Promise<Candidate> {
  const ranked = (await searchAlbums(line.query, RESULTS_PER_LINE))
    .map((album) => ({ album, score: matchScore(line, album) }))
    // Sorting is stable, so hits that score the same keep Spotify's order.
    .sort((a, b) => b.score - a.score);
  const [best] = ranked;
  if (best === undefined) {
    return missed(line.label);
  }
  return {
    label: line.label,
    found: best.album,
    chosen: true,
    score: best.score,
    options: ranked.slice(1).map((entry) => entry.album),
    showing: false,
  };
}

/**
 * The albums the Spotify links in a pasted list name, keyed by id. They are
 * looked up before the searches. See `albumsByIds` for the cost.
 */
async function lookUpLinks(
  lines: ImportLine[],
  report: Progress,
): Promise<Map<string, FoundAlbum>> {
  const ids = lines.flatMap((line) => {
    const id = spotifyAlbumId(line.query);
    return id === null ? [] : [id];
  });
  if (ids.length === 0) {
    return new Map();
  }
  say(report, `Looking up ${String(ids.length)} Spotify links…`);
  return await albumsByIds(ids);
}

/**
 * Resolve a pasted list. A line that is a Spotify link is looked up by id;
 * anything else is searched for, and the best match is offered for review
 * rather than taken on trust.
 */
export async function importPastedList(
  text: string,
  report: Progress,
): Promise<ImportOutcome | null> {
  const lines = parseAlbumLines(text);
  if (lines.length === 0) {
    showError("Nothing to import — the list was empty.");
    return null;
  }
  const candidates: Candidate[] = [];
  // The links are resolved before the loop, so the count covers the searches.
  let searchesLeft = lines.filter(
    (line) => spotifyAlbumId(line.query) === null,
  ).length;
  try {
    await paced(async () => {
      const linked = await lookUpLinks(lines, report);
      for (const [index, line] of lines.entries()) {
        if (pacedStopped()) {
          break;
        }
        const id = spotifyAlbumId(line.query);
        if (id !== null) {
          const album = linked.get(id);
          candidates.push(
            album === undefined ? missed(line.label) : exact(line.label, album),
          );
          continue;
        }
        say(
          report,
          `Looking up ${String(index + 1)} of ${String(lines.length)}: ${line.label}`,
          searchesLeft,
        );
        searchesLeft -= 1;
        candidates.push(await lookUpLine(line));
      }
    });
    return { ...NOTHING, candidates };
  } catch (error) {
    showError(`Could not look up the list: ${describeError(error)}`);
    return null;
  } finally {
    say(report, "");
  }
}

/**
 * Search Spotify for a leftover playlist's name, as for a pasted line.
 *
 * @returns the candidate to add to the review, or null when there is none.
 */
export async function findLeftover(
  name: string,
  report: Progress,
): Promise<Candidate | null> {
  try {
    say(report, `Looking up ${name}…`);
    const candidate = await paced(async () => lookUpLine(importLine(name)));
    if (candidate.found === null) {
      showError(`Spotify has no album called ${name}.`);
      return null;
    }
    return candidate;
  } catch (error) {
    showError(`Could not search for ${name}: ${describeError(error)}`);
    return null;
  } finally {
    say(report, "");
  }
}

/** The review with one row replaced by whatever `change` makes of it. */
function replace(
  candidates: Candidate[],
  index: number,
  change: (candidate: Candidate) => Candidate,
): Candidate[] {
  return candidates.map((candidate, at) =>
    at === index ? change(candidate) : candidate,
  );
}

/** Turn a candidate's checkbox on or off. Unmatched rows cannot be added. */
export function toggleCandidate(
  candidates: Candidate[],
  index: number,
): Candidate[] {
  return replace(candidates, index, (candidate) =>
    candidate.found === null
      ? candidate
      : { ...candidate, chosen: !candidate.chosen },
  );
}

/** Show or hide the other albums the search returned for a line. */
export function showCandidateOptions(
  candidates: Candidate[],
  index: number,
): Candidate[] {
  return replace(candidates, index, (candidate) => ({
    ...candidate,
    showing: !candidate.showing,
  }));
}

/**
 * Answer a line with an album chosen by hand. The caller puts the album it
 * replaces among `options`, so a change of mind is one more tap. A choice by
 * hand scores 1, so the row stops saying it is unsure.
 */
function settle(
  candidate: Candidate,
  found: FoundAlbum,
  options: FoundAlbum[],
): Candidate {
  return {
    ...candidate,
    found,
    options,
    score: 1,
    chosen: true,
    showing: false,
  };
}

/** Take one of the other matches the search returned for a line. */
export function pickCandidate(
  candidates: Candidate[],
  index: number,
  option: number,
): Candidate[] {
  return replace(candidates, index, (candidate) => {
    const picked = required(candidate.options[option], "option");
    const options = [...candidate.options];
    // Only a row with an album of its own offers alternatives to pick from.
    options[option] = required(candidate.found, "matched album");
    return settle(candidate, picked, options);
  });
}

/** Take an album found through the search dialog as a line's answer. */
export function takeSearchResult(
  candidates: Candidate[],
  index: number,
  found: FoundAlbum,
): Candidate[] {
  return replace(candidates, index, (candidate) =>
    settle(
      candidate,
      found,
      candidate.found === null
        ? candidate.options
        : [candidate.found, ...candidate.options],
    ),
  );
}

/**
 * The shelf this import fills, or null. The shelf can be deleted while the
 * review is up, so the id is checked.
 */
function resolveTarget(shelfId: string | null): string | null {
  if (shelfId === null || listById(store().shelves, shelfId) === undefined) {
    return null;
  }
  return shelfId;
}

/**
 * Put every chosen candidate in the library, skipping ones already there.
 *
 * @returns the shelf they were filed on, for the caller to route to. Null
 * when they went on no shelf.
 */
export function addChosen(
  candidates: Candidate[],
  shelfId: string | null,
): string | null {
  const target = resolveTarget(shelfId);
  // One timestamp for the whole import, so "newest first" orders a batch the
  // same way on any machine.
  const now = Date.now();
  for (const candidate of candidates) {
    if (candidate.chosen && candidate.found !== null) {
      addFoundAlbum(store(), candidate.found, now, newId(), target);
    }
  }
  return target;
}
